/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb,
  faChalkboardTeacher,
  faChartLine,
  faBriefcase,
  faArrowLeft,
  faUsers,
  faGlobe,
  faMapMarkerAlt,
  faCertificate
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { formatEmployerName } from '../utils/nameFormatter';
import '../styles/Companies.css';
import '../styles/Professional.css';

const Professional = () => {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProfessionals = async () => {
      try {
        setLoading(true);
        const response = await axios.get(buildApiUrl('/api/companies/list'));
        const allCompanies = response.data?.companies || [];
        const filtered = allCompanies.filter(company => 
          (company.section || '').toLowerCase() === 'professionals'
        );

        setProfessionals(filtered);
      } catch (error) {
        console.error('❌ Error fetching professionals:', error);
        setProfessionals([]);
      } finally {
        setLoading(false);
      }
    };

    loadProfessionals();
  }, []);

  const stats = useMemo(() => ({
    total: professionals.length,
    expertise: professionals.reduce((count, professional) => professional.serviceCategories ? count + 1 : count, 0),
    sessions: professionals.reduce((count, professional) => professional.openPositions ? count + Number(professional.openPositions) : count, 0)
  }), [professionals]);

  const getInitials = (name) => {
    if (!name) return 'P';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="companies-page professional-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faUsers} className="badge-icon" />
            <span>PROFESSIONAL SERVICES</span>
          </div>
          <h1 className="companies-hero-title">
            Connect with Professional Experts <br />
            <span className="gradient-text">Mentors, Trainers & Consultants</span>
          </h1>
          <p className="companies-hero-subtitle">
            Access expert guidance from experienced professionals who can help you achieve your career goals
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.total}+</div>
                <div className="stat-label">Professionals</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.expertise}+</div>
                <div className="stat-label">Expertise Areas</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.sessions}+</div>
                <div className="stat-label">Sessions Available</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      <section className="companies-container" style={{ paddingTop: '3rem' }}>
        <div style={{ marginBottom: '2rem', maxWidth: '760px', margin: '0 auto 2rem auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', textAlign: 'center' }}>
            Featured Mentors, Trainers & Consultants
          </h2>
          <p style={{ color: '#4b5563', fontSize: '16px', margin: 0, textAlign: 'center' }}>
            Book sessions with seasoned professionals ready to guide your growth.
          </p>
        </div>

        {loading ? (
          <div className="companies-loading">
            <div className="loading-spinner"></div>
            <p>Loading professionals...</p>
          </div>
        ) : professionals.length === 0 ? (
          <div className="no-companies">
            <FontAwesomeIcon icon={faUsers} className="no-companies-icon" />
            <h3>No Professionals Listed Yet</h3>
            <p>
              To get highlighted here{' '}
              <Link to="/contact-us" style={{ color: '#4fc3f7', textDecoration: 'underline', fontWeight: '600' }}>
                contact us
              </Link>
            </p>
          </div>
        ) : (
          <div className="companies-grid">
            {professionals.map((professional, index) => {
              const logo = professional.logo || professional.companyLogo || '';
              const rawName = professional.companyName || professional.employerName || 'Professional Mentor';
              const name = formatEmployerName(rawName);
              const location = professional.address || professional.country || '';
              const expertise = professional.serviceCategories || professional.programHighlights || professional.aboutUs || '';
              const certifications = professional.certifications;
              const website = professional.website;

              return (
                <div className="company-card" key={professional._id || professional.id || index}>
                  <div className="company-card-header">
                    <div className="card-header-bg"></div>
                    <div className="company-logo-wrapper">
                      {logo ? (
                        <img
                          src={logo}
                          alt={name}
                          className="company-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="company-logo-placeholder"
                        style={{ display: logo ? 'none' : 'flex' }}
                      >
                        <span className="initials-text">{getInitials(name)}</span>
                      </div>
                    </div>
                    <div className="verified-badge">
                      <FontAwesomeIcon icon={faLightbulb} />
                      <span>Expert Partner</span>
                    </div>
                  </div>

                  <div className="company-card-body">
                    <h3 className="company-name">{name}</h3>
                    {location && (
                      <div className="company-detail">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>{location}</span>
                      </div>
                    )}
                    {certifications && (
                      <div className="company-detail">
                        <FontAwesomeIcon icon={faCertificate} />
                        <span>{certifications}</span>
                      </div>
                    )}
                    {expertise && (
                      <p className="company-description">
                        {expertise.length > 140 ? `${expertise.slice(0, 140)}…` : expertise}
                      </p>
                    )}
                  </div>

                  <div className="company-card-footer">
                    {website ? (
                      <a
                        href={website.startsWith('http') ? website : `https://${website}`}
                        className="view-jobs-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faGlobe} />
                        Visit Profile
                      </a>
                    ) : (
                      <button className="view-jobs-btn" disabled>
                        Session Booking Soon
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Professional Roles Section */}
      <section className="professional-roles-section">
        <div className="professional-roles-container">
          <h2 className="roles-section-title">Explore Professional Services</h2>
          <p className="roles-section-subtitle">
            Connect with experts who can guide, teach, and strategize with you
          </p>
          
          <div className="professional-roles-grid">
            {/* Mentors Card */}
            <div className="professional-role-card mentors-card">
              <div className="role-card-icon orange-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <h3 className="role-card-title">Mentors</h3>
              <p className="role-card-description">
                Guide and inspire the next generation
              </p>
              <div className="role-card-footer">
                <button className="role-card-btn orange-btn">
                  Find a Mentor
                </button>
              </div>
            </div>

            {/* Trainers Card */}
            <div className="professional-role-card trainers-card">
              <div className="role-card-icon teal-icon">
                <FontAwesomeIcon icon={faChalkboardTeacher} />
              </div>
              <h3 className="role-card-title">Trainers</h3>
              <p className="role-card-description">
                Share your expertise and build skills
              </p>
              <div className="role-card-footer">
                <button className="role-card-btn teal-btn">
                  Find a Trainer
                </button>
              </div>
            </div>

            {/* Consultants Card */}
            <div className="professional-role-card consultants-card">
              <div className="role-card-icon orange-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <h3 className="role-card-title">Consultants</h3>
              <p className="role-card-description">
                Provide strategic guidance and expertise
              </p>
              <div className="role-card-footer">
                <button className="role-card-btn orange-btn">
                  Find a Consultant
                </button>
              </div>
            </div>
          </div>

          {/* Additional Info Section */}
          <div className="professional-info-section">
            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <h3>Why Choose Our Professionals?</h3>
              <ul>
                <li>Vetted and experienced experts</li>
                <li>Flexible scheduling options</li>
                <li>Personalized guidance tailored to your needs</li>
                <li>Affordable rates for all budgets</li>
              </ul>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <h3>How It Works</h3>
              <ul>
                <li>Browse through our professional network</li>
                <li>Select the right expert for your needs</li>
                <li>Book a session at your convenience</li>
                <li>Start your journey to success</li>
              </ul>
            </div>
          </div>

          {/* Call to Action */}
          <div className="professional-cta">
            <h2>Ready to Get Started?</h2>
            <p>Join thousands who have transformed their careers with expert guidance</p>
            <button className="professional-cta-btn" onClick={() => navigate('/signup')}>
              Get Started Today
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Professional;

